import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

import { useAuth } from "../../../context/AuthContext";
import { siteApi } from "../../../lib/api";

const SPIN_DURATION_MS = 4200;
const DEFAULT_SEGMENT_COUNT = 8;

const FALLBACK_SEGMENTS = [
  { index: 1, amount: 1000, type: "BONUS" },
  { index: 2, amount: 1, type: "COUPON" },
  { index: 3, amount: 2000, type: "BONUS" },
  { index: 4, amount: 3000, type: "BONUS" },
  { index: 5, amount: 1, type: "COUPON" },
  { index: 6, amount: 5000, type: "BONUS" },
  { index: 7, amount: 10000, type: "BONUS" },
  { index: 8, amount: 2, type: "COUPON" },
];

const numberFrom = (...values) => {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return 0;
};

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const normalizeRotation = (value) => ((value % 360) + 360) % 360;

const getRewardAmount = (item) =>
  numberFrom(
    item?.amount,
    item?.win_amount,
    item?.reward_amount,
    item?.point_amount,
    item?.coupon_amount,
    item?.bonus_amount,
    item?.value,
    item?.trxs_point?.amount,
    item?.trxs_coupon?.amount,
    item?.transaction?.amount
  );

const getRewardUnit = (item) => {
  if (item?.point_amount !== undefined || item?.trxs_point) {
    return "포인트";
  }
  if (item?.coupon_amount !== undefined || item?.trxs_coupon) {
    return "쿠폰";
  }

  const typeText = [
    item?.type,
    item?.reward_type,
    item?.transaction_type,
    item?.reason,
    item?.trxs_type,
    item?.category,
  ]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

  if (
    typeText.includes("MONEY") ||
    typeText.includes("CASH") ||
    typeText.includes("WON") ||
    item?.money !== undefined
  ) {
    return "원";
  }
  if (typeText.includes("POINT") || typeText.includes("BONUS")) {
    return "포인트";
  }
  if (typeText.includes("COUPON")) {
    return "쿠폰";
  }
  return "포인트";
};

const getWheelUnit = (item) => {
  const unit = getRewardUnit(item);
  if (unit === "쿠폰") return "C";
  if (unit === "포인트") return "P";
  return "원";
};

const formatCompactAmount = (amount) => {
  const number = Number(amount);
  if (!Number.isFinite(number) || number <= 0) return "0";
  if (number >= 10000) {
    const value = number / 10000;
    return `${Number.isInteger(value) ? value : value.toFixed(1).replace(/\.0$/, "")}만`;
  }
  if (number >= 1000) {
    const value = number / 1000;
    return `${Number.isInteger(value) ? value : value.toFixed(1).replace(/\.0$/, "")}천`;
  }
  return number.toLocaleString("ko-KR");
};

const formatReward = (item) => {
  const amount = getRewardAmount(item);
  const unit = getRewardUnit(item);

  if (unit === "포인트") return `${amount.toLocaleString("ko-KR")} P`;
  if (unit === "쿠폰") return `${amount.toLocaleString("ko-KR")}장`;
  return `${amount.toLocaleString("ko-KR")}원`;
};

const getWheelLabel = (item, index) => {
  const amount = getRewardAmount(item);
  if (amount <= 0) return `${index + 1}`;
  return `${formatCompactAmount(amount)} ${getWheelUnit(item)}`;
};

const getHistoryDate = (row) => {
  const date = new Date(row?.created_at || row?.createdAt || row?.date || row?.updated_at || "");
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const isWinningHistory = (row) => {
  const reasonText = [row?.reason, row?.memo, row?.type, row?.category, row?.transaction_type]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

  if (reasonText.includes("COUPON_USED")) return false;
  if (
    reasonText.includes("ROULETTE") ||
    reasonText.includes("ROULTEE") ||
    reasonText.includes("COUPON_BONUS") ||
    reasonText.includes("WIN")
  ) {
    return true;
  }
  return Boolean(
    row?.win_amount ||
      row?.reward_amount ||
      row?.point_amount ||
      row?.coupon_amount ||
      row?.trxs_point ||
      row?.trxs_coupon
  );
};

const getApiMessage = (response, fallback) =>
  response?.message || response?.error || response?.data?.message || fallback;

const openLoginModal = () => {
  window.dispatchEvent(
    new CustomEvent("crypto-auth-required", {
      detail: { modalOption: "login" },
    })
  );
};

const Roulette = () => {
  const { user, isLoading: authActionLoading, fetchUserProfile } = useAuth();
  const spinTimeoutRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [segments, setSegments] = useState([]);
  const [couponBalance, setCouponBalance] = useState(0);
  const [featureSetting, setFeatureSetting] = useState(null);
  const [rouletteLoaded, setRouletteLoaded] = useState(false);
  const [history, setHistory] = useState([]);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [dialog, setDialog] = useState(null);

  const authBootstrapping = Boolean(user?.isLoading || authActionLoading);
  const isAuthenticated = Boolean(user?.isAuthenticated);
  const rouletteBlocked = featureSetting?.use_the_roulette === false;
  const rouletteReady = rouletteLoaded && !isLoading && segments.length > 0;
  const canSpin =
    isAuthenticated && !rouletteBlocked && !isLoading && !isSpinning && couponBalance > 0;

  const displaySegments = useMemo(() => {
    const source = segments.length > 0 ? segments : FALLBACK_SEGMENTS;
    const count = Math.max(source.length, DEFAULT_SEGMENT_COUNT);
    return Array.from({ length: count }, (_, index) => source[index] || FALLBACK_SEGMENTS[index % FALLBACK_SEGMENTS.length]);
  }, [segments]);

  const wheelBackground = useMemo(() => {
    const count = Math.max(displaySegments.length, DEFAULT_SEGMENT_COUNT);
    const colors = [
      "#2bf469",
      "#113324",
      "#163f2d",
      "#0b1a20",
      "#45ff7b",
      "#10272d",
      "#1b5238",
      "#0d2027",
    ];

    return `conic-gradient(from -90deg, ${Array.from({ length: count })
      .map(
        (_, index) =>
          `${colors[index % colors.length]} ${(index * 100) / count}% ${((index + 1) * 100) / count}%`
      )
      .join(", ")})`;
  }, [displaySegments.length]);

  const loadHistory = useCallback(async () => {
    if (!isAuthenticated) return;
    setHistoryLoading(true);
    try {
      const params = {
        order_by_name: "id",
        order_by_type: "DESC",
        page: 1,
        limit: 12,
      };
      const [couponResponse, pointResponse] = await Promise.allSettled([
        siteApi.getCouponHistory(params),
        siteApi.getPointHistory({ ...params, reason: "COUPON_BONUS_POINT" }),
      ]);

      const rows = [couponResponse, pointResponse]
        .flatMap((result) =>
          result.status === "fulfilled" && result.value?.success
            ? normalizeRows(result.value.data)
            : []
        )
        .filter(isWinningHistory)
        .sort((a, b) => {
          const aDate = new Date(a?.created_at || a?.createdAt || a?.date || 0).getTime();
          const bDate = new Date(b?.created_at || b?.createdAt || b?.date || 0).getTime();
          return (Number.isNaN(bDate) ? 0 : bDate) - (Number.isNaN(aDate) ? 0 : aDate);
        });

      setHistory((current) => {
        const optimisticRows = current.filter((row) => String(row?.id || "").startsWith("spin-"));
        const seen = new Set(optimisticRows.map((row) => String(row?.id)));
        const mergedRows = [...optimisticRows];

        rows.forEach((row) => {
          const key = String(
            row?.id ||
              `${row?.created_at || row?.createdAt || row?.date}-${getRewardAmount(row)}-${row?.reason || ""}`
          );
          if (!seen.has(key)) {
            seen.add(key);
            mergedRows.push(row);
          }
        });

        return mergedRows.slice(0, 12);
      });
    } finally {
      setHistoryLoading(false);
    }
  }, [isAuthenticated]);

  const loadRoulette = useCallback(async () => {
    if (!isAuthenticated) return;
    setRouletteLoaded(false);
    setIsLoading(true);
    try {
      const [settingResponse, rouletteResponse] = await Promise.allSettled([
        siteApi.getUserSettings(),
        siteApi.roulette.getRewardConfig(),
      ]);

      if (settingResponse.status === "fulfilled" && settingResponse.value?.success) {
        setFeatureSetting(settingResponse.value.data || null);
      }

      if (rouletteResponse.status === "fulfilled" && rouletteResponse.value?.success) {
        const data = rouletteResponse.value.data || {};
        setCouponBalance(numberFrom(data.coupon_balance));
        setSegments(normalizeRows(data.result || data));
      } else {
        const response = rouletteResponse.status === "fulfilled" ? rouletteResponse.value : null;
        setSegments([]);
        setDialog({
          type: "error",
          title: "룰렛 정보를 불러오지 못했습니다.",
          message: getApiMessage(response, "잠시 후 다시 시도해 주세요."),
        });
      }
    } finally {
      setIsLoading(false);
      setRouletteLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setRouletteLoaded(false);
      setSegments([]);
      return;
    }
    loadRoulette();
    loadHistory();
  }, [isAuthenticated, loadHistory, loadRoulette]);

  useEffect(() => {
    return () => {
      if (spinTimeoutRef.current) {
        window.clearTimeout(spinTimeoutRef.current);
      }
    };
  }, []);

  const handleSpin = async () => {
    if (!isAuthenticated) {
      openLoginModal();
      setDialog({
        type: "warning",
        title: "로그인이 필요합니다.",
        message: "룰렛은 로그인 후 이용할 수 있습니다.",
      });
      return;
    }

    if (rouletteBlocked) {
      setDialog({
        type: "warning",
        title: "현재 룰렛을 사용할 수 없습니다.",
        message: "관리자 설정에서 룰렛 이용이 제한되어 있습니다.",
      });
      return;
    }

    if (couponBalance <= 0) {
      setDialog({
        type: "warning",
        title: "룰렛권이 부족합니다.",
        message: "출석체크 등 이벤트로 룰렛권을 획득한 뒤 이용해 주세요.",
      });
      return;
    }

    if (isSpinning || isLoading) return;

    setIsSpinning(true);

    try {
      const response = await siteApi.roulette.spin();
      if (!response?.success) {
        setIsSpinning(false);
        setDialog({
          type: "error",
          title: "룰렛 실행에 실패했습니다.",
          message: getApiMessage(response, "잠시 후 다시 시도해 주세요."),
        });
        return;
      }

      const data = response.data || {};
      const winNumber = Number(data.win_number);
      const winIndex = segments.findIndex((item) => Number(item?.index) === winNumber);
      const safeWinIndex = winIndex >= 0 ? winIndex : 0;
      const winningItem = segments[safeWinIndex] || displaySegments[safeWinIndex] || data;
      const segmentAngle = 360 / Math.max(displaySegments.length, 1);
      const winningSegmentCenter = safeWinIndex * segmentAngle + segmentAngle / 2;
      const desiredRotation = normalizeRotation(-winningSegmentCenter);
      const currentRotation = normalizeRotation(wheelRotation);
      const rotationDelta = normalizeRotation(desiredRotation - currentRotation) || 360;
      const targetRotation = wheelRotation + 1080 + rotationDelta;
      const nextBalance = numberFrom(data.balance, data.coupon_balance, couponBalance - 1);
      const resultPayload = {
        ...winningItem,
        ...data,
        amount: getRewardAmount(data) || getRewardAmount(winningItem),
        type: data?.type || winningItem?.type || "BONUS",
      };

      setWheelRotation(targetRotation);
      setCouponBalance(Math.max(0, nextBalance));

      spinTimeoutRef.current = window.setTimeout(() => {
        setHistory((current) =>
          [
            {
              ...resultPayload,
              id: `spin-${Date.now()}`,
              created_at: new Date().toISOString(),
              reason: "ROULETTE_WIN",
            },
            ...current,
          ].slice(0, 12)
        );
        setDialog({
          type: "success",
          title: "룰렛 당첨",
          message: `${formatReward(resultPayload)} 보상이 지급되었습니다.`,
        });
        setIsSpinning(false);
        loadHistory();
        fetchUserProfile?.({ clearOnError: false, silent: true });
      }, SPIN_DURATION_MS + 120);
    } catch (error) {
      setIsSpinning(false);
      setDialog({
        type: "error",
        title: "룰렛 실행에 실패했습니다.",
        message: error?.response?.data?.message || error?.message || "잠시 후 다시 시도해 주세요.",
      });
    }
  };

  if (authBootstrapping) {
    return (
      <RoulettePage>
        <div className="roulette-shell">
          <RouletteSkeleton />
        </div>
      </RoulettePage>
    );
  }

  return (
    <RoulettePage>
      <div className="roulette-shell">
        <header className="roulette-header">
          <div className="roulette-title-wrap">
            <span className="roulette-icon" aria-hidden="true">
              <RouletteIcon />
            </span>
            <div>
              <span className="roulette-eyebrow">ROULETTE</span>
              <h1>룰렛</h1>
            </div>
          </div>
          <div className="roulette-stats">
            <div>
              <span>보유 룰렛권</span>
              <strong>{isAuthenticated && rouletteLoaded ? couponBalance.toLocaleString("ko-KR") : "-"}장</strong>
            </div>
            <div>
              <span>상태</span>
              <strong>{rouletteBlocked ? "이용 제한" : rouletteReady ? couponBalance > 0 ? "참여 가능" : "대기" : "확인 중"}</strong>
            </div>
          </div>
        </header>

        {!isAuthenticated ? (
          <section className="roulette-login-panel">
            <div>
              <span className="section-kicker">LOGIN REQUIRED</span>
              <h2>로그인 후 룰렛을 이용하세요.</h2>
              <p>룰렛권 보유 수량과 당첨 내역은 계정 기준으로 표시됩니다.</p>
            </div>
            <button type="button" onClick={openLoginModal}>
              로그인
            </button>
          </section>
        ) : (
          <main className="roulette-grid">
            <section className="wheel-panel" aria-label="룰렛 휠">
              {rouletteReady ? (
                <div className={isSpinning ? "wheel-stage is-ready is-spinning" : "wheel-stage is-ready"}>
                  <div className="wheel-pointer" aria-hidden="true" />
                  <div className="wheel-shadow" aria-hidden="true" />
                  <div className="wheel-ring">
                    <div
                      className={isSpinning ? "roulette-wheel is-spinning" : "roulette-wheel"}
                      style={{
                        background: wheelBackground,
                        transform: `rotate(${wheelRotation}deg)`,
                      }}
                      aria-hidden="true"
                    />
                    <div className="wheel-overlay" aria-hidden="true" />
                    <div className="roulette-labels" aria-hidden="true">
                      {displaySegments.map((item, index) => {
                        const segmentAngle = 360 / displaySegments.length;
                        const angle = segmentAngle * index + segmentAngle / 2 + wheelRotation;
                        return (
                          <span
                            className="roulette-segment-label"
                            key={`${item?.index || index}-${index}`}
                            style={{
                              transform: `rotate(${angle}deg) translateY(calc(var(--label-distance) * -1)) rotate(${-angle}deg)`,
                            }}
                          >
                            {getWheelLabel(item, index)}
                          </span>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      className="spin-button"
                      onClick={handleSpin}
                      disabled={!canSpin}
                      aria-label="룰렛 돌리기"
                    >
                      {isSpinning ? <span className="spin-loader" /> : <PlayIcon />}
                      <span>{isSpinning ? "회전중" : "START"}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="wheel-loading" aria-label="룰렛 보상 로딩">
                  <span />
                </div>
              )}
            </section>

            <aside className="roulette-side-panel">
              <section className="history-panel">
                <div className="panel-heading">
                  <span>최근 당첨</span>
                  <strong>{history.length}건</strong>
                </div>
                {historyLoading ? (
                  <div className="history-skeleton" aria-label="룰렛 내역 로딩">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span key={index} />
                    ))}
                  </div>
                ) : history.length > 0 ? (
                  <div className="history-list">
                    {history.map((row, index) => (
                      <div className="history-row" key={`${row?.id || index}-history`}>
                        <div>
                          <strong>{formatReward(row)}</strong>
                          <span>{getHistoryDate(row)}</span>
                        </div>
                        <em>{getRewardUnit(row)}</em>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-history">최근 당첨 내역이 없습니다.</div>
                )}
              </section>
            </aside>
          </main>
        )}
      </div>

      {dialog && (
        <div className="roulette-dialog-backdrop" role="presentation" onClick={() => setDialog(null)}>
          <div
            className={`roulette-dialog is-${dialog.type || "info"}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="roulette-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="dialog-icon" aria-hidden="true">
              {dialog.type === "success" ? "✓" : "!"}
            </span>
            <h2 id="roulette-dialog-title">{dialog.title}</h2>
            <p>{dialog.message}</p>
            <button type="button" onClick={() => setDialog(null)}>
              확인
            </button>
          </div>
        </div>
      )}
    </RoulettePage>
  );
};

const RouletteIcon = () => (
  <svg viewBox="0 0 48 48" focusable="false">
    <circle cx="24" cy="24" r="18" fill="none" stroke="currentColor" strokeWidth="4" />
    <circle cx="24" cy="24" r="5" fill="currentColor" />
    <path d="M24 6v8M24 34v8M6 24h8M34 24h8M11.3 11.3l5.7 5.7M31 31l5.7 5.7M36.7 11.3 31 17M17 31l-5.7 5.7" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
    <path d="M11 8.8v14.4c0 1.2 1.3 1.9 2.3 1.3l11-7.2c.9-.6.9-2 0-2.6l-11-7.2C12.3 6.9 11 7.6 11 8.8Z" fill="currentColor" />
  </svg>
);

const pulse = keyframes`
  0% { opacity: 0.45; transform: translateX(-30%); }
  50% { opacity: 1; }
  100% { opacity: 0.45; transform: translateX(30%); }
`;

const dialogIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const wheelEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.94);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`;

const wheelLoadingPulse = keyframes`
  0% {
    opacity: 0.55;
    transform: scale(0.96) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1) rotate(180deg);
  }
  100% {
    opacity: 0.55;
    transform: scale(0.96) rotate(360deg);
  }
`;

const wheelAura = keyframes`
  0% { transform: rotate(0deg); opacity: 0.28; }
  50% { opacity: 0.48; }
  100% { transform: rotate(360deg); opacity: 0.28; }
`;

const pointerTick = keyframes`
  from { transform: translateX(-50%) translateY(0); }
  to { transform: translateX(-50%) translateY(4px); }
`;

const spinPulse = keyframes`
  to { transform: rotate(360deg); }
`;

const RouletteSkeleton = () => (
  <div className="roulette-skeleton">
    <span className="wide" />
    <div>
      <span />
      <span />
    </div>
  </div>
);

const RoulettePage = styled.div`
  min-height: calc(100vh - 84px);
  background: #102b33;
  color: #f4f8fb;
  padding: 32px 18px 72px;

  .roulette-shell {
    width: min(1180px, 100%);
    margin: 0 auto;
  }

  .roulette-header,
  .roulette-login-panel,
  .wheel-panel,
  .roulette-side-panel section {
    border: 1px solid rgba(158, 189, 202, 0.18);
    background: #0a2028;
    box-shadow: 0 16px 42px rgba(0, 0, 0, 0.18);
  }

  .roulette-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 22px;
    border-radius: 18px;
    padding: 24px;
    margin-bottom: 22px;
  }

  .roulette-title-wrap {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  .roulette-icon {
    width: 58px;
    height: 58px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: 18px;
    color: #4dff78;
    background: rgba(49, 232, 102, 0.12);
    border: 1px solid rgba(77, 255, 120, 0.28);
  }

  .roulette-icon svg {
    width: 30px;
    height: 30px;
  }

  .roulette-eyebrow,
  .section-kicker {
    display: block;
    margin-bottom: 4px;
    color: #4dff78;
    font-size: 13px;
    font-weight: 900;
    letter-spacing: 0;
  }

  h1,
  h2,
  p {
    margin: 0;
  }

  h1 {
    font-size: clamp(34px, 4vw, 54px);
    line-height: 0.95;
    letter-spacing: 0;
  }

  .roulette-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 1fr));
    gap: 10px;
  }

  .roulette-stats div {
    min-height: 72px;
    border-radius: 14px;
    padding: 14px 18px;
    background: #071820;
    border: 1px solid rgba(158, 189, 202, 0.14);
  }

  .roulette-stats span,
  .panel-heading strong,
  .history-row span,
  .roulette-login-panel p {
    color: #9eb8c5;
  }

  .roulette-stats span {
    display: block;
    font-size: 14px;
    margin-bottom: 6px;
  }

  .roulette-stats strong {
    font-size: 24px;
    font-weight: 900;
  }

  .roulette-login-panel {
    min-height: 320px;
    border-radius: 18px;
    padding: 34px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .roulette-login-panel h2 {
    font-size: clamp(28px, 4vw, 42px);
    margin-bottom: 12px;
  }

  .roulette-login-panel p {
    font-size: 18px;
    line-height: 1.6;
  }

  .roulette-login-panel button,
  .spin-button,
  .roulette-dialog button {
    border: 0;
    cursor: pointer;
    font: inherit;
    font-weight: 900;
    color: #061016;
    background: #36e86a;
    transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
  }

  .roulette-login-panel button {
    min-width: 150px;
    height: 58px;
    border-radius: 14px;
  }

  .roulette-login-panel button:hover,
  .roulette-dialog button:hover {
    transform: translateY(-2px);
    background: #4dff78;
    box-shadow: 0 14px 26px rgba(54, 232, 106, 0.26);
  }

  .roulette-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.55fr);
    gap: 22px;
    align-items: start;
  }

  .wheel-panel,
  .roulette-side-panel section {
    border-radius: 18px;
  }

  .wheel-panel {
    overflow: hidden;
    padding: 22px;
    display: grid;
    place-items: center;
  }

  .spin-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    box-shadow: none;
  }

  .spin-button:disabled {
    transform: translate(-50%, -50%);
  }

  .wheel-stage {
    --wheel-size: min(520px, 72vw);
    --label-distance: clamp(92px, 18vw, 172px);
    position: relative;
    width: var(--wheel-size);
    height: calc(var(--wheel-size) + 62px);
    margin: 28px auto 8px;
    display: grid;
    place-items: center;
    perspective: 1000px;
  }

  .wheel-stage.is-ready {
    animation: ${wheelEnter} 0.44s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .wheel-loading {
    --wheel-size: min(520px, 72vw);
    position: relative;
    width: var(--wheel-size);
    height: calc(var(--wheel-size) + 62px);
    margin: 28px auto 8px;
    display: grid;
    place-items: center;
  }

  .wheel-loading::before {
    content: "";
    position: absolute;
    width: var(--wheel-size);
    height: var(--wheel-size);
    border-radius: 50%;
    background:
      radial-gradient(circle at center, #0a2028 0 30%, transparent 31%),
      conic-gradient(
        from -90deg,
        rgba(77, 255, 120, 0.22) 0 12%,
        #102b33 12% 25%,
        rgba(77, 255, 120, 0.12) 25% 37%,
        #071820 37% 50%,
        rgba(77, 255, 120, 0.2) 50% 62%,
        #102b33 62% 75%,
        rgba(77, 255, 120, 0.1) 75% 87%,
        #071820 87% 100%
      );
    border: 14px solid #132d36;
    box-shadow:
      inset 0 0 0 8px rgba(255, 255, 255, 0.035),
      0 24px 56px rgba(0, 0, 0, 0.28);
    animation: ${wheelLoadingPulse} 1.65s ease-in-out infinite;
  }

  .wheel-loading span {
    position: relative;
    z-index: 1;
    width: clamp(132px, 17vw, 168px);
    height: clamp(132px, 17vw, 168px);
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(77, 255, 120, 0.88), rgba(35, 206, 86, 0.92));
    border: 7px solid rgba(7, 24, 32, 0.62);
    box-shadow:
      0 18px 36px rgba(54, 232, 106, 0.18),
      inset 0 -10px 18px rgba(3, 20, 12, 0.18);
  }

  .wheel-shadow {
    position: absolute;
    top: calc(50% + 170px);
    width: 68%;
    height: 36px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.36);
    filter: blur(20px);
    transform: rotateX(68deg);
  }

  .wheel-pointer {
    position: absolute;
    top: 12px;
    left: 50%;
    z-index: 7;
    width: 0;
    height: 0;
    border-left: 18px solid transparent;
    border-right: 18px solid transparent;
    border-top: 34px solid #f8fafc;
    filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.42));
    transform: translateX(-50%);
  }

  .wheel-stage.is-spinning .wheel-pointer {
    animation: ${pointerTick} 0.18s ease-in-out infinite alternate;
  }

  .wheel-ring {
    position: relative;
    box-sizing: border-box;
    width: var(--wheel-size);
    height: var(--wheel-size);
    border-radius: 50%;
    transform: rotateX(7deg);
    transform-style: preserve-3d;
    background: #06151b;
    border: 14px solid #132d36;
    box-shadow:
      inset 0 0 0 8px rgba(255, 255, 255, 0.04),
      inset 0 -24px 42px rgba(0, 0, 0, 0.34),
      0 24px 56px rgba(0, 0, 0, 0.34);
  }

  .wheel-ring::before {
    content: "";
    position: absolute;
    inset: -22px;
    border-radius: 50%;
    z-index: -1;
    pointer-events: none;
    background: conic-gradient(
      from 0deg,
      transparent 0 18%,
      rgba(77, 255, 120, 0.2) 24%,
      transparent 31% 62%,
      rgba(77, 255, 120, 0.18) 68%,
      transparent 76% 100%
    );
    filter: blur(18px);
    opacity: 0.16;
  }

  .wheel-stage.is-spinning .wheel-ring::before {
    animation: ${wheelAura} 1.4s linear infinite;
  }

  .roulette-wheel,
  .wheel-overlay,
  .roulette-labels,
  .roulette-segment-label {
    pointer-events: none;
  }

  .roulette-wheel,
  .wheel-overlay,
  .roulette-labels {
    position: absolute;
    inset: 0;
    border-radius: 50%;
  }

  .roulette-wheel {
    transition: none;
    will-change: transform;
  }

  .wheel-stage.is-spinning .roulette-wheel,
  .wheel-stage.is-spinning .roulette-segment-label {
    transition: transform ${SPIN_DURATION_MS}ms cubic-bezier(0.12, 0.74, 0.08, 1);
  }

  .wheel-overlay {
    pointer-events: none;
    background:
      radial-gradient(circle at center, transparent 0 20%, rgba(0, 0, 0, 0.14) 21% 43%, transparent 44%),
      radial-gradient(circle at 36% 28%, rgba(255, 255, 255, 0.26), transparent 20%),
      radial-gradient(circle at center, transparent 0 68%, rgba(2, 8, 12, 0.46) 70% 100%);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  }

  .roulette-labels {
    z-index: 2;
    transition: opacity 0.2s ease;
  }

  .roulette-segment-label {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 74px;
    margin-left: -37px;
    margin-top: -14px;
    color: #f7fbff;
    font-size: clamp(12px, 1.45vw, 16px);
    font-weight: 900;
    line-height: 1;
    text-align: center;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
    transition: none;
    will-change: transform;
  }

  .spin-button {
    appearance: none;
    position: absolute;
    inset: 50% auto auto 50%;
    z-index: 12;
    width: clamp(152px, 19vw, 192px);
    height: clamp(152px, 19vw, 192px);
    padding: 0;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transform: translate(-50%, -50%);
    border: 7px solid rgba(7, 24, 32, 0.62);
    background: radial-gradient(circle at 34% 24%, #76ff95 0 16%, #44f471 44%, #24d85a 100%);
    box-shadow:
      0 20px 36px rgba(54, 232, 106, 0.25),
      0 0 0 12px rgba(77, 255, 120, 0.08),
      inset 0 -10px 18px rgba(3, 20, 12, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.18);
    isolation: isolate;
    line-height: 1;
    outline: none;
    overflow: visible;
    touch-action: manipulation;
    user-select: none;
  }

  .spin-button::before {
    content: "";
    position: absolute;
    inset: -20px;
    border-radius: 50%;
    pointer-events: none;
  }

  .spin-button::after {
    content: "";
    position: absolute;
    inset: 13px;
    z-index: 0;
    border-radius: 50%;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.13), rgba(6, 16, 22, 0.05));
  }

  .spin-button:hover:not(:disabled) {
    transform: translate(-50%, -50%);
    background: radial-gradient(circle at 34% 24%, #8affaa 0 16%, #52ff7e 44%, #26df61 100%);
    box-shadow:
      0 26px 48px rgba(54, 232, 106, 0.34),
      0 0 0 14px rgba(77, 255, 120, 0.1),
      inset 0 -10px 18px rgba(3, 20, 12, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.18);
    filter: brightness(1.03);
  }

  .spin-button:active:not(:disabled) {
    transform: translate(-50%, -50%);
    filter: brightness(0.96);
  }

  .spin-button:disabled:hover {
    transform: translate(-50%, -50%);
  }

  .spin-button svg {
    width: 38px;
    height: 38px;
  }

  .spin-button svg,
  .spin-button span,
  .spin-loader {
    pointer-events: none;
    position: relative;
    z-index: 1;
  }

  .spin-button span:last-child {
    font-size: 15px;
    letter-spacing: 0;
  }

  .spin-loader {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 4px solid rgba(6, 16, 22, 0.24);
    border-top-color: #061016;
    animation: ${spinPulse} 0.8s linear infinite;
  }

  .roulette-side-panel {
    display: grid;
    gap: 18px;
  }

  .roulette-side-panel section {
    padding: 20px;
  }

  .panel-heading {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    margin-bottom: 16px;
  }

  .panel-heading span {
    font-size: 20px;
    font-weight: 900;
  }

  .history-list {
    display: grid;
    gap: 10px;
  }

  .history-row {
    border-radius: 12px;
    background: #071820;
    border: 1px solid rgba(158, 189, 202, 0.12);
  }

  .history-list {
    max-height: 360px;
    overflow: auto;
    padding-right: 3px;
  }

  .history-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 13px 14px;
  }

  .history-row div {
    min-width: 0;
  }

  .history-row strong,
  .history-row span {
    display: block;
  }

  .history-row strong {
    color: #f7fbff;
    font-size: 15px;
    margin-bottom: 2px;
  }

  .history-row span {
    font-size: 13px;
  }

  .history-row em {
    color: #4dff78;
    font-style: normal;
    font-size: 13px;
    font-weight: 900;
  }

  .empty-history {
    min-height: 180px;
    display: grid;
    place-items: center;
    border-radius: 14px;
    background: #071820;
    color: #9eb8c5;
    font-weight: 800;
  }

  .history-skeleton {
    display: grid;
    gap: 12px;
  }

  .history-skeleton span,
  .roulette-skeleton span {
    position: relative;
    overflow: hidden;
    display: block;
    border-radius: 12px;
    background: #18323b;
  }

  .history-skeleton span::after,
  .roulette-skeleton span::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
    animation: ${pulse} 1.35s ease-in-out infinite;
  }

  .history-skeleton span {
    height: 58px;
  }

  .roulette-skeleton {
    display: grid;
    gap: 22px;
  }

  .roulette-skeleton .wide {
    height: 110px;
  }

  .roulette-skeleton div {
    display: grid;
    grid-template-columns: 1.4fr 0.8fr;
    gap: 22px;
  }

  .roulette-skeleton div span {
    height: 520px;
  }

  .history-list::-webkit-scrollbar {
    width: 5px;
  }

  .history-list::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: rgba(77, 255, 120, 0.45);
  }

  .roulette-dialog-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1500;
    display: grid;
    place-items: center;
    padding: 18px;
    background: rgba(2, 8, 12, 0.66);
    backdrop-filter: blur(6px);
  }

  .roulette-dialog {
    width: min(420px, 100%);
    border-radius: 18px;
    padding: 28px;
    color: #f7fbff;
    background: #0a2028;
    border: 1px solid rgba(158, 189, 202, 0.22);
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.46);
    text-align: center;
    animation: ${dialogIn} 0.18s ease both;
  }

  .dialog-icon {
    width: 58px;
    height: 58px;
    margin: 0 auto 16px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 32px;
    font-weight: 900;
    color: #061016;
    background: #36e86a;
  }

  .roulette-dialog.is-error .dialog-icon,
  .roulette-dialog.is-warning .dialog-icon {
    color: #ffdce0;
    background: rgba(255, 92, 112, 0.18);
    border: 1px solid rgba(255, 112, 128, 0.34);
  }

  .roulette-dialog h2 {
    font-size: 24px;
    margin-bottom: 8px;
  }

  .roulette-dialog p {
    color: #bdd0da;
    font-size: 16px;
    line-height: 1.55;
  }

  .roulette-dialog button {
    width: 100%;
    height: 52px;
    border-radius: 12px;
    margin-top: 22px;
  }

  @media (max-width: 960px) {
    .roulette-header,
    .roulette-login-panel {
      align-items: stretch;
      flex-direction: column;
    }

    .roulette-stats,
    .roulette-grid,
    .roulette-skeleton div {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 620px) {
    padding: 18px 10px 56px;

    .roulette-header,
    .wheel-panel,
    .roulette-side-panel section,
    .roulette-login-panel {
      border-radius: 14px;
      padding: 16px;
    }

    .roulette-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
    }

    .roulette-stats {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .roulette-stats div {
      min-height: 66px;
      padding: 12px;
    }

    .roulette-stats strong {
      font-size: 19px;
    }

    .wheel-stage,
    .wheel-loading {
      --wheel-size: min(330px, calc(100vw - 72px));
      --label-distance: clamp(72px, 28vw, 106px);
      height: calc(var(--wheel-size) + 34px);
      margin: 10px auto 4px;
    }

    .wheel-loading::before {
      border-width: 9px;
    }

    .wheel-loading span {
      width: 112px;
      height: 112px;
      border-width: 5px;
    }

    .wheel-ring {
      border-width: 9px;
    }

    .wheel-pointer {
      top: 6px;
      border-left-width: 13px;
      border-right-width: 13px;
      border-top-width: 26px;
    }

    .roulette-segment-label {
      width: 58px;
      margin-left: -29px;
      font-size: 11px;
    }

    .spin-button {
      width: 124px;
      height: 124px;
      border-width: 5px;
    }

    .spin-button svg {
      width: 30px;
      height: 30px;
    }

    .spin-button span:last-child {
      font-size: 12px;
    }

    .history-list {
      max-height: 300px;
    }
  }
`;

export default Roulette;
